/**
 * Push a chat lead into an account's contacts.
 *
 * The write goes through the same `importContactRows` service every other import
 * uses, which is the reason this dialog is thin: consent is not decided here. A
 * lead who left an email on a marketing site has not opted in to marketing, and
 * the shared service is what guarantees that — it starts the contact
 * transactional, lifts it to informational only where a jurisdiction rule allows,
 * and never grants a marketing purpose. The result panel below says which of
 * those happened rather than reporting a bare "done".
 */
import { useEffect, useMemo, useState } from 'react';
import { Building2, CheckCircle2, Loader2, Search } from 'lucide-react';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  cn,
} from '@respectly/ui';
import type { ChatLeadDto, ChatLeadToContactResultDto } from '@respectly/shared-types';
import { useChatLeadsStore } from '../../stores/useChatLeadsStore';

interface PickerAccount {
  id: string;
  name: string;
  slug: string;
  ownerEmail: string | null;
}

export function AddToContactsDialog({
  lead,
  open,
  onClose,
}: {
  lead: ChatLeadDto | null;
  open: boolean;
  onClose: () => void;
}) {
  const adding = useChatLeadsStore((s) => s.adding);
  const addToContacts = useChatLeadsStore((s) => s.addToContacts);

  const [accounts, setAccounts] = useState<PickerAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [result, setResult] = useState<ChatLeadToContactResultDto | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedId(null);
    setResult(null);
    setFailure(null);
  }, [open, lead?.id]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      setLoadingAccounts(true);
      try {
        const api = getApiClient();
        const params = new URLSearchParams({ page: '1', limit: '500' });
        const res = await api.get<{ data: PickerAccount[] }>(
          `${API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.LIST}?${params.toString()}`
        );
        if (!cancelled) setAccounts(res?.data ?? []);
      } catch {
        if (!cancelled) setAccounts([]);
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.slug.toLowerCase().includes(q) ||
        (a.ownerEmail?.toLowerCase().includes(q) ?? false)
    );
  }, [accounts, search]);

  const submit = async (): Promise<void> => {
    if (!lead || !selectedId) return;
    setFailure(null);
    const res = await addToContacts(lead.id, selectedId);
    if (!res) {
      setFailure(useChatLeadsStore.getState().error ?? 'That did not go through.');
      return;
    }
    setResult(res);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add to contacts</DialogTitle>
          <DialogDescription>
            {lead
              ? `${lead.name ?? lead.email} will be added to the account you pick.`
              : 'Pick an account.'}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 py-2">
            <div className="flex items-start gap-2 rounded-xl border border-teal-200 bg-teal-50 p-3 dark:border-teal-900 dark:bg-teal-950/30">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-600 dark:text-teal-400" />
              <div className="text-sm">
                <p className="font-medium text-teal-900 dark:text-teal-200">
                  {result.created > 0
                    ? 'Contact created'
                    : result.updated > 0
                      ? 'Contact updated'
                      : result.skipped > 0
                        ? 'Already in this account'
                        : 'Nothing was written'}
                </p>
                <p className="mt-1 text-teal-800 dark:text-teal-300">
                  {result.autoGrantedInformational
                    ? 'Informational consent was granted automatically by a jurisdiction rule. Marketing consent was not — that still has to be collected.'
                    : 'No consent was granted. This contact is transactional only until someone collects it.'}
                </p>
              </div>
            </div>
            {result.errors.length > 0 && (
              <ul className="space-y-1 text-xs text-amber-700 dark:text-amber-400">
                {result.errors.map((e, i) => (
                  <li key={`${e.field}-${i}`}>
                    <span className="font-mono">{e.field}</span> — {e.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                placeholder="Search accounts"
                className="pl-9"
              />
            </div>

            <div className="max-h-64 space-y-1 overflow-y-auto">
              {loadingAccounts ? (
                <p className="flex items-center gap-2 px-2 py-4 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading accounts&hellip;
                </p>
              ) : filtered.length === 0 ? (
                <p className="px-2 py-4 text-sm text-slate-500">No accounts match that.</p>
              ) : (
                filtered.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setSelectedId(a.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors',
                      selectedId === a.id
                        ? 'bg-teal-50 ring-1 ring-teal-500/30 dark:bg-teal-950/40'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                    )}
                  >
                    <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-slate-900 dark:text-slate-100">
                        {a.name}
                      </span>
                      <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                        {a.ownerEmail ?? a.slug}
                      </span>
                    </span>
                    {selectedId === a.id && (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-teal-600" />
                    )}
                  </button>
                ))
              )}
            </div>

            {failure && <p className="text-xs text-red-600 dark:text-red-400">{failure}</p>}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {result ? 'Done' : 'Cancel'}
          </Button>
          {!result && (
            <Button onClick={() => void submit()} disabled={!selectedId || adding}>
              {adding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add contact
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
